import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';
import CountryStateSelect from './CountryStateSelect';

// Persist dismissal so the prompt does not nag on every page view. Dismissal is
// per-device and never blocks access (E1.6 — grandfathered clients).
const DISMISS_KEY = 'coachsight.profileCompletionPrompt.dismissed';

/**
 * Non-blocking prompt (E1.6 / CSECR-19) asking a grandfathered client to supply
 * the now-mandatory DOB / state / country. It appears only when the client's
 * profile is incomplete and has not been dismissed. Saving the values updates
 * the record (PATCH /api/clients/me) and the prompt stops appearing; dismissing
 * it simply hides it. Neither path ever blocks the client's use of the app.
 */
const ProfileCompletionBanner: React.FC = () => {
  const [incomplete, setIncomplete] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => localStorage.getItem(DISMISS_KEY) === '1');
  const [dob, setDob] = useState('');
  const [country, setCountry] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load the client's own profile to decide whether to show the prompt. Any
  // failure is non-fatal: we simply do not show the prompt.
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('authToken') || '';
    (async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}${endpoints.main.clients.me}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const d = await res.json();
        if (cancelled) return;
        setIncomplete(!!d.profile_incomplete);
        // Prefill any values that already exist so the client only fills the gaps.
        if (d.date_of_birth) setDob(String(d.date_of_birth).split('T')[0]);
        if (d.country) setCountry(d.country);
        if (d.state_province) setStateProvince(d.state_province);
      } catch {
        /* non-fatal — prompt stays hidden */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!incomplete || dismissed) return null;

  const dobInFuture = !!dob && new Date(dob) > new Date();
  const canSave = !!dob && !dobInFuture && !!country && !!stateProvince.trim();

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const handleSave = async () => {
    if (!canSave) {
      setError('Enter your date of birth, country, and state/province.');
      return;
    }
    setSaving(true);
    setError('');
    const token = localStorage.getItem('authToken') || '';
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.clients.me}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          date_of_birth: dob,
          country,
          state_province: stateProvince.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not save your details. Please try again.');
      // Saved successfully — the profile is now complete, so hide the prompt.
      setIncomplete(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-6 mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-200">Complete your profile</p>
          <p className="text-xs text-amber-200/80 mt-0.5">
            Please add your date of birth, country, and state/province to keep your CoachSight profile up to date. You can keep using CoachSight either way.
          </p>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label htmlFor="pcb-dob" className="block text-xs font-medium text-gray-400 mb-1">Date of Birth</label>
              <input
                id="pcb-dob"
                type="date"
                value={dob}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setDob(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200 ${
                  dobInFuture ? 'border-red-500' : 'border-dark-border'
                }`}
              />
              {dobInFuture && <p className="text-red-400 text-xs mt-1">Date of birth cannot be in the future</p>}
            </div>
            <CountryStateSelect
              variant="dark"
              idPrefix="pcb"
              country={country}
              stateProvince={stateProvince}
              onCountryChange={(iso) => { setCountry(iso); setStateProvince(''); }}
              onStateChange={setStateProvince}
            />
          </div>

          {error && <p className="text-red-400 text-xs mt-2">{error}</p>}

          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !canSave}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="text-xs text-amber-200/80 hover:text-amber-100 transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-amber-300/70 hover:text-amber-100 flex-shrink-0 transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default ProfileCompletionBanner;
